// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { CheckCircle2, Loader2, Sparkles } from 'lucide-react';

/**
 * Inline "AI suggestion" panel shown under a field or card while an AI polish
 * runs and once its result is in. Renders nothing when idle.
 */
export function AiSuggestionBox({
  loading,
  result,
  onAccept,
  onRegenerate,
  onDismiss,
  className,
}: {
  loading: boolean;
  result: string | null;
  onAccept: () => void;
  onRegenerate: () => void;
  onDismiss: () => void;
  className?: string;
}) {
  if (!loading && !result) return null;

  return (
    <div
      className={`rounded-md border border-ai-border/30 bg-ai-muted px-2.5 py-2 ${className ?? ''}`}
    >
      <div className="flex items-center gap-1.5">
        {loading ? (
          <Loader2 className="h-3 w-3 animate-spin text-ai" />
        ) : (
          <Sparkles className="h-3 w-3 shrink-0 text-ai" />
        )}
        <span className="text-[12px] font-medium uppercase tracking-wide text-ai">
          {loading ? 'Polishing...' : 'AI suggestion'}
        </span>
      </div>
      {result && (
        <>
          <p className="mt-1 whitespace-pre-line text-[14px] leading-relaxed text-foreground">
            {result}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={onAccept}
              className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-ai-foreground transition-colors hover:bg-ai/90 focus:outline-none focus:ring-2 focus:ring-ai"
            >
              <CheckCircle2 className="h-3 w-3" />
              Use this
            </button>
            <button
              type="button"
              onClick={onRegenerate}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-ai transition-colors hover:bg-ai-muted hover:text-ai disabled:opacity-50"
            >
              <Sparkles className="h-3 w-3" />
              Regenerate
            </button>
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              Dismiss
            </button>
          </div>
        </>
      )}
    </div>
  );
}
